import { isInternalUser } from '@steerfi/shared';
import {
  IconDotsVertical,
  IconLogout,
  IconMoon,
  IconSun,
  IconUserCircle,
} from '@tabler/icons-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ApiVersionResponse, getApiVersion } from '@/api/version';
import { CopyableText } from '@/components/copyable-text';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const { logout, user: authUser, isAuthenticated } = useKeycloakAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [apiVersionInfo, setApiVersionInfo] = useState<ApiVersionResponse | null>(null);
  const { copy, isCopied } = useCopyToClipboard();
  const isInternal = isInternalUser(authUser?.roles);

  useEffect(() => {
    if (!isAuthenticated || !isInternal) return;
    getApiVersion()
      .then(setApiVersionInfo)
      .catch(() => {
        // non-critical
      });
  }, [isAuthenticated, isInternal]);

  const handleLogout = async () => {
    try {
      await logout();
      if (isMobile) setOpenMobile(false);
      navigate('/login');
    } catch (_error) {
      // Logout failed - user will remain on current page
    }
  };

  const handleSettingsClick = () => {
    if (isMobile) setOpenMobile(false);
    navigate('/settings');
  };

  return (
    <SidebarMenu data-testid="user-menu">
      <SidebarMenuItem className="flex flex-col gap-0.5">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center w-full gap-2 p-2 rounded-md text-left hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
            <Avatar className="h-8 w-8 rounded-sm grayscale">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="rounded-sm">CN</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user.name}</span>
              <span className="text-muted-foreground truncate text-xs">{user.email}</span>
            </div>
            <IconDotsVertical className="ml-auto size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 overflow-hidden p-0"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
            style={{ zIndex: 9999 }}
          >
            <div className="p-1">
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={handleSettingsClick}>
                  <IconUserCircle />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  className="justify-between"
                >
                  <div className="flex items-center gap-2">
                    {theme === 'dark' ? <IconSun /> : <IconMoon />}
                    Theme
                  </div>
                  <span className="text-muted-foreground text-xs capitalize">
                    {theme ?? 'light'}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <IconLogout />
                Log out
              </DropdownMenuItem>
            </div>

            {isInternal && (
              <div className="flex flex-col items-start gap-0.5 border-t border-border bg-muted/50 px-2 py-1.5">
                <CopyableText
                  variant="muted"
                  title="Copy version"
                  label={`UI ${__APP_VERSION__}`}
                  copyValue={`UI ${__APP_VERSION__}`}
                  isCopied={isCopied('ui')}
                  onCopy={(value, event) => copy(value, 'ui', event)}
                />
                {apiVersionInfo && (
                  <CopyableText
                    variant="muted"
                    title="Copy version"
                    label={`API ${apiVersionInfo.version}`}
                    copyValue={`API ${apiVersionInfo.version}`}
                    isCopied={isCopied('api')}
                    onCopy={(value, event) => copy(value, 'api', event)}
                  />
                )}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Link
          to="/terms-of-use"
          className="text-muted-foreground hover:text-foreground ml-2 w-fit self-start text-[10px] transition-colors"
          onClick={() => {
            if (isMobile) setOpenMobile(false);
          }}
        >
          Terms of Use
        </Link>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
